(ns ui.components.select.select
  (:require
   [clojure.edn :as edn]
   [clojure.string :as string]
   [common.localization :as loc]
   [common.select-util :as csu]
   [common.util :as cu]
   [oops.core :as oops]
   [reagent.core :as r]
   ["react-select" :default Select :refer (components)]
   ["react-select/creatable" :default CreatableSelect]
   ["react-window" :refer (FixedSizeList)]
   ["react-window-infinite-loader" :as ReactWindowInfiniteLoader]
   [ui.dom-globals :as dg]
   [ui.util :as uu]))

(def default-max-hits-per-page 50)
(def item-size 40)
(def min-menu-height item-size)
(def max-menu-height (* item-size 8))

(defn js-value->value
  [encode? v]
  (if encode?
    (edn/read-string v)
    v))

(defn js-opt->opt
  [encode? js-opt]
  (assoc js-opt :value (js-value->value encode? (:value js-opt))))

(defn val-and-options
  [{:keys [is-multi? encode? value-opt]}]
  (let [val-options (if is-multi?
                      (map (partial js-opt->opt encode?) value-opt)
                      (js-opt->opt encode? value-opt))]
    (if is-multi?
      [(map :value val-options) val-options]
      [(:value val-options) val-options])))

(defn value-opt->js-value-opt
  [{:keys [encode? value-opt]}]
  (if encode?
    (assoc value-opt :value (pr-str (:value value-opt)))
    value-opt))

(defn view
  [{:keys [options
           is-multi?
           _value
           _on-change
           _on-focus
           _disabled?
           _create!
           _placeholder
           _max-hits-per-page                                                                       ;; ??
           _menu-placement
           _auto-focus?
           _block-scroll-on-menu-open?
           _is-clearable?
           _open-menu-on-focus?
           _class
           _menu-portal-target]}]
  (let [is-multi? (if (boolean? is-multi?)
                    is-multi?
                    false)
        first-value (->> options
                         (keep :value)
                         (first))
        encode? (and (not (number? first-value))
                     (not (string? first-value))
                     (not (boolean? first-value)))
        ;debug-state* (r/atom {})
        ]
    (fn
      [{:keys [value
               options
               on-change
               on-blur
               on-focus
               disabled?
               create!
               placeholder
               menu-placement
               auto-focus?
               block-scroll-on-menu-open?
               is-clearable?
               on-menu-close!
               on-menu-open!
               open-menu-on-focus?
               class
               small?
               menu-portal-target]}]
      ;; TODO - remove when debug done
      #_(assert
         (if is-multi?
           (or (empty? value)
               (->> value
                    (every? (fn [opt]
                              (= [:value :label]
                                 (keys (select-keys opt [:value :label])))))))
           (or (nil? value)
               (= [:value :label]
                  (keys (select-keys value [:value :label]))))))

      (let [to-js-option (fn [opt]
                           (try
                             (when opt
                               (let [opt (if (or (:isDisabled opt) (:disabled? opt))
                                           (-> opt
                                               (dissoc :disabled?)
                                               (assoc :isDisabled true))
                                           opt)]
                                 (value-opt->js-value-opt {:encode?   encode?
                                                           :value-opt opt})))
                             (catch js/Object o
                               (js/console.log "Error " o))))
            creatable? (fn? create!)
            value-opt (csu/value-to-opt is-multi? value options)
            value-opt-js (if is-multi?
                           (->> value-opt
                                (mapv to-js-option))
                           (to-js-option value-opt))]
        ;(js/console.log " Select : " value " -> " value-opt " = " value-opt-js)
        [:>
         (if creatable?
           CreatableSelect
           Select)
         (merge {:value                 value-opt-js
                 :options               (map to-js-option options)
                 :menuPortalTarget      (or (when (string? menu-portal-target)
                                              (dg/element-by-id menu-portal-target))
                                            (dg/body))
                 ;; TODO -> confirm we want to have this fixed
                 :menuPosition          "fixed"                                                     ;; fixed | absolute
                 :classNames            {:menuPortal         (fn [_js-state]
                                                               "z-[40000000] pointer-events-auto ")
                                         :menuList           (fn [_js-state]
                                                               (when small?
                                                                 "text-sm p-0 m-0"))
                                         :multiValue         (constantly "bg-complement-regular text-complement-regular-text")
                                         :multiValueRemove   (constantly "bg-complement-regular text-complement-regular-text")
                                         :control            (constantly
                                                              (uu/cm
                                                               "pl-2 min-h-10 bg-input-light text-input-light-text border border-solid border-input-light-border-paler hover:border-input-light-border focus:border-input-light-border outline-none shadow-none"
                                                               "pl-2 min-h-10 bg-input-light text-input-light-text border border-solid border-input-light-border-paler hover:border-input-light-border focus:border-input-light-border outline-none shadow-none"
                                                               class))
                                         :menu               (fn [_js-state]
                                                               (if small?
                                                                 (constantly "z-[40000000] bg-input-light ")
                                                                 (constantly "z-[40000000] bg-input-light")))
                                         :singleValue        (constantly "text-input-light-text")
                                         :indicatorSeparator (constantly "hidden")
                                         :input              (constantly "min-w-[60px]")
                                         :option             (fn [js-state]
                                                               (let [is-disabled? (boolean (oops/oget js-state "isDisabled"))
                                                                     selected? (or (= (:value value-opt-js)
                                                                                      (aget js-state "value"))
                                                                                   (= (pr-str (:value value-opt-js))
                                                                                      (aget js-state "value")))]
                                                                 (cond is-disabled?
                                                                       "text-base-light-text-paler bg-input-light opacity-60 cursor-not-allowed"
                                                                       (oops/oget js-state "isFocused")
                                                                       "hover:bg-input-gloomy hover:text-input-gloomy-text bg-input-gloomy text-input-gloomy-text disabled:text-disabled-light-text disabled:bg-disabled-light"
                                                                       selected?
                                                                       "text-success-gloomy-text bg-success-gloomy hover:bg-input-gloomy hover:text-input-gloomy-text disabled:text-disabled-light-text disabled:bg-disabled-light"
                                                                       :else
                                                                       "text-input-light-text bg-input-light hover:bg-input-gloomy hover:text-input-gloomy-text disabled:text-disabled-light-text disabled:bg-disabled-light")))
                                         :valueContainer     (if is-multi?
                                                               ;; does it make sense to pass 'class' here? passing it to :control seems more reasonable (red border for error etc)
                                                               (constantly (uu/cm class "flex flex-row flex-wrap gap-1.5 py-1 px-0 *:p-0"))
                                                               (constantly class))
                                         :multiValueLabel    (constantly "bg-complement-regular text-complement-regular-text")}
                 :onMenuClose           (fn []
                                          ;(reset! state-atom default-state)
                                          ;(reset! scroll-offset-state 0)
                                          ;(reset! should-focus-option-state false)
                                          (when (fn? on-menu-close!)
                                            (on-menu-close!)))
                 :onMenuOpen            (fn [& _args]
                                          (when (fn? on-menu-open!)
                                            (on-menu-open!)))
                 :filterOption          (fn [js-option query]
                                          ;; filter out selected options so that they are not shown in dropdown
                                          (let [opt-value (oops/oget js-option "value")
                                                opt-label (oops/oget js-option "label")
                                                contains-label? (or (cu/blank-value? query)
                                                                    (string/includes? (cu/str-trim-lcase opt-label)
                                                                                      (cu/str-trim-lcase query)))]
                                            (if-not is-multi?
                                              contains-label?
                                              (and contains-label?
                                                   (or (empty? value-opt)
                                                       ;; TODO - if value has many selections - using set could be faster
                                                       (not-any? #{opt-value} value-opt))))))
                 :isMulti               is-multi?
                 :isClearable           is-clearable?
                 :isDisabled            disabled?
                 :autoFocus             auto-focus?
                 :openMenuOnFocus       (boolean open-menu-on-focus?)
                 :createOptionPosition  "first"
                 :placeholder           (or placeholder "")
                 :menuShouldBlockScroll (if (boolean? block-scroll-on-menu-open?)
                                          block-scroll-on-menu-open?
                                          true)
                 :onCreateOption        (fn [query]
                                          (when (fn? create!)
                                            (create! (string/trim query))))
                 :noOptionsMessage      (fn []
                                          (loc/tr :no-options))
                 ; Todo fix view jumping when opening near bottom of screen
                 :menuPlacement         (or menu-placement "auto")
                 ;; TODO : FIX ME
                 :onChange              (fn [js-val _js-opts]
                                          (let [val (js->clj js-val :keywordize-keys true)
                                                [vals opts] (val-and-options {:is-multi? is-multi?
                                                                              :encode?   encode?
                                                                              :value-opt val})]
                                            ;(js/console.log "Select change : " js-val " = " js-opts " : " vals " > " opts)
                                            (on-change vals opts)))
                 :onBlur                (fn [_]
                                          (when (fn? on-blur)
                                            (on-blur)))
                 :onFocus               (fn [_]
                                          (when (fn? on-focus)
                                            (on-focus)))
                 :components
                 {:SingleValue (r/reactify-component
                                (fn [{:keys [children data selectProps] :as props}]
                                  (let [_domain (aget data "secondaryLabel")]
                                    [:> (.-SingleValue components)
                                     props
                                     [:div
                                      {:class "flex flex-row items-center"}
                                      (if (aget selectProps "menuIsOpen")
                                        ""
                                        children)]])))

                  #_:Input       #_(r/reactify-component
                                    (fn [props]
                                      [:> (.-Input components)
                                       (merge (js->clj props :keywordize-keys true)
                                              {:onKeyDown (fn [e]
                                                            (let [ctrlKey (aget e "ctrlKey")
                                                                  o? (= "o" (aget e "key"))]
                                                              (when (and ctrlKey o?)
                                                                (swap! debug-state* assoc :force-menu-open? (not (:force-menu-open? @debug-state*))))))})]))

                  #_:MultiValueLabel #_(r/reactify-component
                                        (fn [{:keys [data innerProps selectProps children] :as props}]
                                          [:> (.-MultiValueLabel components)
                                           props
                                           [:div
                                            {:class "flex flex-row items-center"}
                                            children]]))
                  #_:MultiValue  #_(r/reactify-component
                                    (fn [props]
                                      (let [js-data (:data props)
                                            options (:options props)
                                            data (js->clj js-data :keywordize-keys true)
                                            label (or (and (map? data)
                                                           (:label data))
                                                      (->> options
                                                           (keep (fn [js-opt]
                                                                   (let [opt (js->clj js-opt :keywordize-keys true)]
                                                                     (when (= (:value opt) data)
                                                                       (:label opt)))))
                                                           (first)))]
                                        [:> (.-SingleValue components)
                                         props
                                         [:div
                                          {:class "flex flex-row items-center
                               text-sm
                                border rounded
                               pr-2 pl-2
                               bg-complement-regular text-complement-regular-text"}
                                          label]])))
                  #_:Option          #_(r/reactify-component
                                        (fn [props]
                                          (let [js-data (:data props)
                                                {:keys [icon value label] :as data} (js->clj js-data :keywordize-keys true)]
                                            [:> (.-Option components)
                                             props
                                             [:div
                                              {:class "flex flex-row gap-2 items-center justify-items-center"}
                                              (when icon
                                                ;[tooltip/view
                                                [:i.fal
                                                 {:class icon}]
                                                #_[:div
                                                   {:class "max-w-40"}
                                                   "long sample tooltip text that can span over multiple elements within the menu dropdown - does it work ?"]
                                                ;]
                                                )
                                              label]])))
                  #_:MenuList
                  #_(r/reactify-component
                     (fn [{:keys [options children focusedOption] :as menu-list-props}]
                       (let [option-count (or (.-length children) 0)
                             menu-height (min (max (* option-count item-size) min-menu-height) max-menu-height)

                             is-item-loaded (fn [i]
                                              (< i (count options)))
                             no-options (fn []
                                          (and (not (:loading? @state-atom))
                                               (= option-count 0)))
                             has-next-page? (:has-next-page? @state-atom)
                             item-count (if has-next-page?
                                          (+ option-count 1)
                                          option-count)]
                         [:div
                          [:> ReactWindowInfiniteLoader
                           {:isItemLoaded is-item-loaded
                            :itemCount    item-count
                            :threshold    2
                            :loadMoreItems
                            (fn []
                              (let [start-index (:start-index @state-atom)
                                    search-term (:search-term @state-atom)
                                    previous-options (:options @state-atom)]
                                (-> (get-options {:search-term       search-term
                                                  :start-index       start-index
                                                  :max-hits-per-page max-hits-per-page})
                                    (p/then (fn [{:keys [options has-next-page?]}]

                                              (swap! state-atom assoc :loading? false)

                                              (let [new-options (vec (concat previous-options options))]
                                                (swap! state-atom assoc :options new-options)
                                                (swap! state-atom assoc :has-next-page? has-next-page?)
                                                (swap! state-atom assoc :start-index (+ start-index max-hits-per-page)))))
                                    (p/catch
                                     (fn []
                                       (swap! state-atom assoc :loading? false))))))}
                           (fn [list-js-props]
                             (r/as-element
                              [:> FixedSizeList
                               {:height              menu-height
                                :itemCount           item-count
                                :itemSize            item-size
                                :onItemsRendered     (.-onItemsRendered list-js-props)
                                :ref                 (fn [dom-node]
                                                       (when (and dom-node @should-focus-option-state)
                                                         (->> focusedOption
                                                              (.indexOf options)
                                                              (.scrollToItem dom-node))
                                                         (reset! should-focus-option-state false))
                                                       ((.-ref list-js-props) dom-node))
                                :initialScrollOffset @scroll-offset-state
                                :onScroll            (fn [js-event]
                                                       (let [scroll-offset (.-scrollOffset js-event)
                                                             scroll-direction (.-scrollDirection js-event)]
                                                         (when-not (and (= scroll-direction "forward")
                                                                        (= scroll-offset 0))
                                                           (reset! scroll-offset-state scroll-offset))))}
                               (fn [item-js-props]
                                 (let [style (.-style item-js-props)
                                       index (.-index item-js-props)]
                                   (r/as-element [:div
                                                  {:style style}
                                                  (cond
                                                    (no-options)
                                                    [:div
                                                     {:class (h/<class style/loading)}
                                                     "No results"]

                                                    (is-item-loaded index)
                                                    (get children index)

                                                    :else
                                                    [:div
                                                     {:class (h/<class style/loading)}
                                                     "Loading..."])])))]))]])))}
                 ;:onKeyDown             #(reset! should-focus-option-state true)
                 ;:onKeyUp               #(reset! should-focus-option-state true)

                 #_:onKeyDown             #_(fn [k]
                                              (let [{:keys [ctrlKey key]} (js->clj k :keywordize-keys true)]
                                                (when (and ctrlKey
                                                           (= key "o"))
                                                  (js/console.log "Force open : " key)
                                                  (swap! debug-state* assoc :force-menu-open? (not (:force-menu-open? @debug-state*))))))}
                #_(when (:force-menu-open? @debug-state*)
                    (js/console.log "Force open : " key)
                    {:menuIsOpen true}))]))))
